import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Radio, Space, message, Divider, Typography } from 'antd';
import { CreditCardOutlined, ShoppingCartOutlined } from '@ant-design/icons';

import { Order } from '../../types';
import { formatAmount } from '../../utils';

const { Title, Text } = Typography;

const Cashier: React.FC = () => {

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('jiaoe');
  const [currentOrder, setCurrentOrder] = useState<Order | null>(null);

  // 模拟订单数据
  const mockOrder: Order = React.useMemo(() => ({
    order_no: 'ORD' + Date.now(),
    amount: '150000', // 1500.00元（以分为单位）
    payer_name: '上海数据科技有限公司',
    payee_name: '杭州云数据服务有限公司',
    description: '金融行业客户画像数据包',
    status: 'pending',
    created_at: new Date().toISOString(),
  }), []);

  useEffect(() => {
    // 模拟从上游系统接收订单
    setCurrentOrder(mockOrder);
  }, [mockOrder]);

  // 订单详情表格数据
  const orderColumns = [
    {
      title: '商品名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '单价',
      dataIndex: 'price',
      key: 'price',
      render: (price: string) => `¥${formatAmount(price)}`,
    },
    {
      title: '数量',
      dataIndex: 'quantity',
      key: 'quantity',
    },
    {
      title: '小计',
      dataIndex: 'subtotal',
      key: 'subtotal',
      render: (subtotal: string) => (
        <Text strong style={{ color: '#1890ff' }}>
          ¥{formatAmount(subtotal)}
        </Text>
      ),
    },
  ];

  const orderData = [
    {
      key: '1',
      name: '金融行业客户画像数据包',
      price: '150000',
      quantity: 1,
      subtotal: '150000',
    },
  ];

  // 支付方式选项
  const paymentMethods = [
    {
      value: 'jiaoe',
      label: '交e保支付',
      icon: <CreditCardOutlined />,
      description: '使用交通银行交e保虚拟账户支付',
      recommended: true,
    },
    {
      value: 'bank_transfer',
      label: '银行转账',
      icon: <CreditCardOutlined />,
      description: '通过银行转账方式支付',
      disabled: true,
    },
  ];

  const handlePayment = () => {
    if (!currentOrder) {
      message.error('订单信息异常，请刷新页面重试');
      return;
    }

    if (selectedPaymentMethod !== 'jiaoe') {
      message.warning('当前仅支持交e保支付方式');
      return;
    }

    // 将订单信息存储在 sessionStorage 中，供新页面使用
    sessionStorage.setItem('currentOrder', JSON.stringify(currentOrder));
    sessionStorage.setItem('selectedPaymentMethod', selectedPaymentMethod);

    // 打开新的支付扫码授权页面，在新标签页中打开
    const newWindow = window.open('/#/payment-process/qrcode-auth', '_blank');
    if (newWindow) {
      newWindow.focus();
    } else {
      message.error('无法打开支付页面，请检查浏览器设置');
    }
  };

  if (!currentOrder) {
    return (
      <div className="loading-container">
        <Text>正在加载订单信息...</Text>
      </div>
    );
  }

  return (
    <div>
      <Title level={2} style={{ textAlign: 'center', marginBottom: 32 }}>
        <ShoppingCartOutlined style={{ marginRight: 12, color: '#1890ff' }} />
        收银台
      </Title>

      <div style={{ maxWidth: 800, margin: '0 auto' }}>
        {/* 订单信息卡片 */}
        <Card 
          title="订单信息" 
          style={{ marginBottom: 24 }}
          headStyle={{ background: '#fafafa', fontWeight: 600 }}
        >
          <div style={{ marginBottom: 16 }}>
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">订单号：</Text>
                <Text strong>{currentOrder.order_no}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">付款方：</Text>
                <Text>{currentOrder.payer_name}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">收款方：</Text>
                <Text>{currentOrder.payee_name}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">订单描述：</Text>
                <Text>{currentOrder.description}</Text>
              </div>
            </Space>
          </div>

          <Divider />

          <Table
            columns={orderColumns}
            dataSource={orderData}
            pagination={false}
            size="small"
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={3}>
                  <Text strong>总计</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <Text 
                    strong 
                    style={{ fontSize: 18, color: '#ff4d4f' }}
                  >
                    ¥{formatAmount(currentOrder.amount)}
                  </Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
        </Card>

        {/* 支付方式选择卡片 */}
        <Card 
          title="选择支付方式" 
          style={{ marginBottom: 24 }}
          headStyle={{ background: '#fafafa', fontWeight: 600 }}
        >
          <Radio.Group
            value={selectedPaymentMethod}
            onChange={(e: any) => setSelectedPaymentMethod(e.target.value)}
            style={{ width: '100%' }}
          >
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              {paymentMethods.map((method) => (
                <Card
                  key={method.value}
                  size="small"
                  hoverable={!method.disabled}
                  style={{
                    border: selectedPaymentMethod === method.value ? '2px solid #1890ff' : '1px solid #e8e8e8',
                    opacity: method.disabled ? 0.5 : 1,
                    cursor: method.disabled ? 'not-allowed' : 'pointer',
                  }}
                  onClick={() => !method.disabled && setSelectedPaymentMethod(method.value)}
                >
                  <Radio 
                    value={method.value} 
                    disabled={method.disabled}
                    style={{ width: '100%' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <span style={{ fontSize: 20, marginRight: 12 }}>
                        {method.icon}
                      </span>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <Text strong style={{ marginRight: 8 }}>
                            {method.label}
                          </Text>
                          {method.recommended && (
                            <span 
                              style={{
                                background: '#52c41a',
                                color: '#fff',
                                padding: '2px 6px',
                                borderRadius: 3,
                                fontSize: 12,
                              }}
                            >
                              推荐
                            </span>
                          )}
                        </div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {method.description}
                        </Text>
                      </div>
                    </div>
                  </Radio>
                </Card>
              ))}
            </Space>
          </Radio.Group>
        </Card>

        {/* 确认支付按钮 */}
        <div style={{ textAlign: 'center' }}>
          <Button
            type="primary"
            size="large"
            onClick={handlePayment}
            style={{
              width: 200,
              height: 50,
              fontSize: 16,
              borderRadius: 6,
            }}
            disabled={!selectedPaymentMethod}
          >
            确认支付 ¥{formatAmount(currentOrder.amount)}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Cashier;